import { CSSProperties, PointerEvent as ReactPointerEvent, RefObject, useMemo, useRef, useState } from 'react';

// The shared swipe engine (design brief §6): pointer events and CSS
// transforms only, no gesture library. One card at a time; "forward"/"back"
// are logical (reading-direction-relative), not "left"/"right" — in RTL
// (Arabic) forward is a physical left swipe, so every caller gets mirroring
// for free instead of re-deriving it.
export type SwipeDir = 'forward' | 'back';

interface UseSwipeOptions {
  /** Called once a drag or a fling(dir) call commits — after the card has
   * visually left, so the caller can swap in the next one. */
  onCommit: (dir: SwipeDir) => void;
  /** False blocks that direction: the gesture springs back instead (e.g. a
   * mood card before any face is picked). Defaults to always allowed. */
  canCommit?: (dir: SwipeDir) => boolean;
  /** Reading direction right-to-left (Arabic today). Mirrors which physical
   * side is "forward". */
  rtl?: boolean;
  /** True while something else owns the gesture (e.g. mid-animation on a
   * different card) — ignores pointer input entirely. */
  disabled?: boolean;
}

interface UseSwipeResult {
  /** Attach to the card element — needed to measure its width and to know
   * what to animate. */
  ref: RefObject<HTMLElement>;
  /** Spread onto the card element. */
  handlers: {
    onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void;
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void;
  };
  /** Spread onto the same card element — transform/transition while
   * dragging, springing back or flinging off-screen. */
  style: CSSProperties;
  /** Which stamp to fade in, and how far (0–1), while dragging or flinging. */
  stamp: { dir: SwipeDir | null; progress: number };
  /** Programmatic commit for buttons and keys — same animation as a real
   * swipe, so both paths feel identical. */
  fling: (dir: SwipeDir) => void;
  /** True once a fling is in flight (buttons/keys should ignore input until
   * onCommit fires for the next card). */
  flinging: boolean;
}

const EDGE_GUARD = 24; // px from a screen edge: leave the OS back-gesture alone
const AXIS_LOCK = 12; // px of |dx| before the vertical-vs-horizontal call is made
const COMMIT_FRACTION = 0.3; // of card width
const FLICK_VELOCITY = 0.6; // px/ms
const FLICK_MIN_DX = 40; // px
const SPRING_MS = 320;
const FLING_MS = 240;
const REDUCED_MS = 160;

function prefersReducedMotion() {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

function physicalSign(dir: SwipeDir, rtl: boolean): 1 | -1 {
  // LTR: forward = right (+1). RTL: forward = towards the inline end, which
  // reads right-to-left, so forward is physically left (-1).
  const forwardIsRight = !rtl;
  return (dir === 'forward') === forwardIsRight ? 1 : -1;
}

export function useSwipe({ onCommit, canCommit, rtl = false, disabled = false }: UseSwipeOptions): UseSwipeResult {
  const ref = useRef<HTMLElement | null>(null);
  const [dragX, setDragX] = useState(0);
  const [phase, setPhase] = useState<'idle' | 'dragging' | 'settling' | 'flinging'>('idle');
  const drag = useRef<{ pointerId: number; startX: number; startY: number; startTime: number; axis: 'undecided' | 'horizontal' | 'vertical'; width: number } | null>(null);

  function withinEdgeGuard(x: number) {
    return x < EDGE_GUARD || x > window.innerWidth - EDGE_GUARD;
  }
  function isFormControl(el: EventTarget | null) {
    return el instanceof Element && !!el.closest('button, input, textarea, a, select');
  }

  function settle(toX: number, nextPhase: 'settling' | 'idle') {
    setPhase(nextPhase);
    setDragX(toX);
  }

  function resolveCommit(dx: number, dt: number) {
    const el = ref.current;
    const width = el?.getBoundingClientRect().width || 300;
    const velocity = dt > 0 ? dx / dt : 0;
    const farEnough = Math.abs(dx) > COMMIT_FRACTION * width;
    const flicked = Math.abs(velocity) > FLICK_VELOCITY && Math.abs(dx) > FLICK_MIN_DX;
    if (!farEnough && !flicked) return null;
    const forwardIsRight = !rtl;
    const dir: SwipeDir = (dx > 0) === forwardIsRight ? 'forward' : 'back';
    if (canCommit && !canCommit(dir)) return null;
    return dir;
  }

  function onPointerDown(e: ReactPointerEvent<HTMLElement>) {
    if (disabled || phase === 'flinging') return;
    if (isFormControl(e.target)) return;
    if (withinEdgeGuard(e.clientX)) return;
    drag.current = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, startTime: performance.now(), axis: 'undecided', width: ref.current?.getBoundingClientRect().width || 300 };
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setPhase('dragging');
  }

  function onPointerMove(e: ReactPointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId || phase !== 'dragging') return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (d.axis === 'undecided') {
      if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
      d.axis = Math.abs(dy) > 1.5 * Math.abs(dx) ? 'vertical' : 'horizontal';
      if (d.axis === 'vertical') {
        drag.current = null;
        setPhase('idle');
        return; // let the page scroll; we never touched transform
      }
    }
    if (d.axis !== 'horizontal') return;
    setDragX(dx);
  }

  function endDrag(e: ReactPointerEvent<HTMLElement>) {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    if (phase !== 'dragging') return;
    const dx = e.clientX - d.startX;
    const dt = performance.now() - d.startTime;
    const dir = resolveCommit(dx, dt);
    if (dir) fling(dir, dx);
    else settle(0, 'settling');
  }

  function onPointerUp(e: ReactPointerEvent<HTMLElement>) {
    endDrag(e);
  }
  function onPointerCancel() {
    drag.current = null;
    if (phase === 'dragging') settle(0, 'settling');
  }

  function fling(dir: SwipeDir, fromX?: number) {
    if (phase === 'flinging') return; // already committing — ignore a second call before onCommit fires
    if (canCommit && !canCommit(dir)) {
      settle(0, 'settling');
      return;
    }
    const reduced = prefersReducedMotion();
    setPhase('flinging');
    if (reduced) {
      // No rotation or flight: fade in place, then advance.
      setDragX(fromX ?? 0);
      setTimeout(() => onCommit(dir), REDUCED_MS);
      return;
    }
    const width = ref.current?.getBoundingClientRect().width || window.innerWidth;
    const target = physicalSign(dir, rtl) * Math.max(window.innerWidth * 1.2, width * 1.2);
    setDragX(target);
    setTimeout(() => onCommit(dir), FLING_MS);
  }

  const reduced = useMemo(prefersReducedMotion, []);
  const rotation = reduced ? 0 : dragX / 18;
  const transitionMs = phase === 'dragging' ? 0 : phase === 'flinging' ? (reduced ? REDUCED_MS : FLING_MS) : SPRING_MS;
  const style: CSSProperties = {
    transform: reduced && phase === 'flinging' ? undefined : `translateX(${dragX}px) rotate(${rotation}deg)`,
    opacity: reduced && phase === 'flinging' ? 0 : undefined,
    transition: transitionMs ? `transform ${transitionMs}ms cubic-bezier(.2,.8,.2,1), opacity ${transitionMs}ms linear` : 'none',
    touchAction: 'pan-y',
  };

  const forwardIsRight = !rtl;
  const stampDir: SwipeDir | null = dragX === 0 ? null : (dragX > 0) === forwardIsRight ? 'forward' : 'back';
  const stampProgress = Math.min(1, Math.abs(dragX) / ((ref.current?.getBoundingClientRect().width || 300) * COMMIT_FRACTION));

  return {
    ref: ref as RefObject<HTMLElement>,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    style,
    stamp: { dir: phase === 'idle' ? null : stampDir, progress: phase === 'idle' ? 0 : stampProgress },
    fling: (dir: SwipeDir) => fling(dir),
    flinging: phase === 'flinging',
  };
}
